/**
 * ProgramSolutions — Solutions tab (Market group). Read + light-select view of
 * the program's active SolutionTaxonomy, redesigned per Figma "Solutions family,
 * expanded" (DEV-411 / DEV-410):
 *   - intro stats (accepted families / technologies) + instructional copy
 *   - taxonomy provenance + "Regenerate taxonomy"
 *   - a 2-column grid of solution-family cards
 *   - each card opens the detail overlay (SolutionFamilyDetailModal)
 *   - "Search existing Tech Tree" (TechTreePickerModal) adds a family by hand,
 *     anchored on a technology picked from the taxonomy
 *
 * Each family's accept toggle flips backend acceptance and (via the backend)
 * syncs the family's leaf technologies into Program.selected_technologies —
 * which Market Search then picks up as its tech filter.
 */
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, Search } from 'lucide-react'

import Button from '../../components/ui/Button'
import Switch from '../../components/ui/Switch'
import { tokenManager } from '../../lib/api'
import { useViewportContext } from '../../hooks/useViewportContext'
import SolutionFamilyDetailModal, {
  FamilyAgeRisk,
  FamilyStats,
  type FamilyResearchTriage,
  type FamilyStatus,
  type SolutionFamily,
} from './SolutionFamilyDetailModal'
import TechTreePickerModal from './TechTreePickerModal'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface TaxonomyJob {
  progress_percentage?: number
  current_step_label?: string
}

interface SolutionTaxonomy {
  id: string
  program: number
  mode: 'fast' | 'slow' | 'hybrid' | 'manual'
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  summary: string
  families: SolutionFamily[]
  job?: TaxonomyJob | null
  created_by?: { id: number; username?: string; full_name?: string } | null
  created_at: string
  source_document?: { template_name?: string } | null
}

interface ResearchResponse extends FamilyResearchTriage {
  family_id: string
}

interface ProgramSolutionsProps {
  /** Render without the page chrome (padding / max-width) for inline embedding. */
  embedded?: boolean
  /** Program id override; falls back to the route param when omitted. */
  programId?: string
}

const ProgramSolutions = ({ embedded = false, programId: programIdProp }: ProgramSolutionsProps = {}) => {
  const params = useParams<{ programId: string }>()
  const programId = programIdProp ?? params.programId
  const [programName, setProgramName] = useState('')
  const [taxonomy, setTaxonomy] = useState<SolutionTaxonomy | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingFamilyIds, setPendingFamilyIds] = useState<Set<string>>(new Set())
  const [researchBusyIds, setResearchBusyIds] = useState<Set<string>>(new Set())
  const [researchResults, setResearchResults] = useState<Record<string, ResearchResponse>>({})
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (!programId) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      const token = tokenManager.getAccessToken()
      try {
        const [progRes, taxRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/programs/${programId}/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(
            `${API_BASE_URL}/api/solution-taxonomies/?program=${programId}&is_active=true`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ])
        if (!progRes.ok) throw new Error(`Program fetch failed (${progRes.status})`)
        const prog = await progRes.json()
        if (cancelled) return
        setProgramName(prog.name || '')

        if (!taxRes.ok) throw new Error(`Taxonomy fetch failed (${taxRes.status})`)
        const taxData = await taxRes.json()
        const active: SolutionTaxonomy | undefined =
          taxData.results?.[0] ?? (Array.isArray(taxData) ? taxData[0] : undefined)
        if (cancelled) return
        setTaxonomy(active ?? null)
        // A fresh hand-picked taxonomy has nothing in it yet — open the picker.
        if (active?.mode === 'manual' && (active.families ?? []).length === 0) {
          setPickerOpen(true)
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [programId])

  const acceptedFamilies = useMemo(
    () => (taxonomy?.families ?? []).filter((f) => f.status === 'accepted'),
    [taxonomy],
  )

  const totalTechIdsSelected = useMemo(() => {
    const ids = new Set<number>()
    for (const f of acceptedFamilies) {
      if (f.technology_id != null) ids.add(f.technology_id)
    }
    return ids.size
  }, [acceptedFamilies])

  useViewportContext(
    'program_solutions',
    () => ({
      program_id: programId,
      program_name: programName,
      taxonomy_id: taxonomy?.id ?? null,
      taxonomy_status: taxonomy?.status ?? null,
      taxonomy_mode: taxonomy?.mode ?? null,
      family_count: taxonomy?.families.length ?? 0,
      accepted_family_count: acceptedFamilies.length,
      accepted_families: acceptedFamilies.map((f) => ({ id: f.id, name: f.name })),
      tech_ids_flowing_to_market_search: totalTechIdsSelected,
    }),
    [programId, programName, taxonomy, acceptedFamilies, totalTechIdsSelected],
  )

  const familyStatusByTechId = useMemo(() => {
    const map = new Map<number, FamilyStatus>()
    for (const f of taxonomy?.families ?? []) {
      if (f.technology_id == null) continue
      // Several families can share an anchor; any accepted one wins.
      if (map.get(f.technology_id) !== 'accepted') map.set(f.technology_id, f.status)
    }
    return map
  }, [taxonomy])

  const selectedFamily = useMemo(
    () => taxonomy?.families.find((f) => f.id === selectedFamilyId) ?? null,
    [taxonomy, selectedFamilyId],
  )

  const toggleAccept = async (family: SolutionFamily, nextChecked: boolean) => {
    if (pendingFamilyIds.has(family.id)) return
    const prevStatus = family.status
    const optimisticStatus: SolutionFamily['status'] = nextChecked ? 'accepted' : 'rejected'

    setTaxonomy((prev) =>
      prev
        ? {
            ...prev,
            families: prev.families.map((f) =>
              f.id === family.id ? { ...f, status: optimisticStatus } : f,
            ),
          }
        : prev,
    )
    setPendingFamilyIds((prev) => new Set(prev).add(family.id))

    const endpoint = nextChecked ? 'accept' : 'reject'
    const token = tokenManager.getAccessToken()
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/solution-families/${family.id}/${endpoint}/`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      )
      if (!res.ok) throw new Error(`${endpoint} failed (${res.status})`)
      const updated = await res.json()
      setTaxonomy((prev) =>
        prev
          ? {
              ...prev,
              families: prev.families.map((f) =>
                f.id === family.id ? { ...f, ...updated } : f,
              ),
            }
          : prev,
      )
    } catch (e) {
      setTaxonomy((prev) =>
        prev
          ? {
              ...prev,
              families: prev.families.map((f) =>
                f.id === family.id ? { ...f, status: prevStatus } : f,
              ),
            }
          : prev,
      )
      setError((e as Error).message)
    } finally {
      setPendingFamilyIds((prev) => {
        const next = new Set(prev)
        next.delete(family.id)
        return next
      })
    }
  }

  const runResearch = async (family: SolutionFamily) => {
    if (researchBusyIds.has(family.id)) return
    const techLabel = family.technology_name ? `"${family.technology_name}"` : 'the anchor technology'
    const ok = window.confirm(
      `Run deep research for ${techLabel} in "${family.name}"?\n\nThis kicks off a background Perplexity + OpenAI deep-research run (~10-15 min). Skipped if recent research exists.`,
    )
    if (!ok) return

    setResearchBusyIds((prev) => new Set(prev).add(family.id))
    const token = tokenManager.getAccessToken()
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/solution-families/${family.id}/research/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      )
      if (!res.ok && res.status !== 202) {
        throw new Error(`research failed (${res.status})`)
      }
      // A 202 kickoff may carry an empty body; only record a triage summary
      // when the response actually parses as JSON.
      const body: ResearchResponse | null = await res.json().catch(() => null)
      if (body) setResearchResults((prev) => ({ ...prev, [family.id]: body }))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setResearchBusyIds((prev) => {
        const next = new Set(prev)
        next.delete(family.id)
        return next
      })
    }
  }

  // Add (or accept the existing) family anchored on a tech picked from the tree.
  // Throws so the picker can show the error inline.
  const addFamilyFromTech = async (technologyId: number) => {
    if (!taxonomy) return
    const token = tokenManager.getAccessToken()
    const res = await fetch(
      `${API_BASE_URL}/api/solution-taxonomies/${taxonomy.id}/add-family/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ technology_id: technologyId }),
      },
    )
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new Error(body?.detail || `Add failed (${res.status})`)
    }
    const family: SolutionFamily = await res.json()
    setTaxonomy((prev) => {
      if (!prev) return prev
      const exists = prev.families.some((f) => f.id === family.id)
      return {
        ...prev,
        families: exists
          ? prev.families.map((f) => (f.id === family.id ? { ...f, ...family } : f))
          : [...prev.families, family],
      }
    })
  }

  // Regenerate the fast taxonomy from scratch (rare — only after material CNS
  // edits). Deactivates the prior and starts a fresh fast pass.
  const regenerate = async () => {
    if (!programId) return
    const ok = window.confirm(
      'Regenerate solution families from the current Capability Need Statement?' +
        '\n\nThis replaces the current set with a fresh fast pass.',
    )
    if (!ok) return
    const token = tokenManager.getAccessToken()
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/solution-taxonomy/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode: 'fast' }),
        },
      )
      if (!res.ok && res.status !== 202) {
        throw new Error(`regenerate failed (${res.status})`)
      }
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const shell = embedded ? '' : 'p-8 max-w-6xl mx-auto'

  if (loading) {
    return (
      <div className={`${embedded ? '' : 'p-8'} flex items-center gap-2 text-muted-foreground`}>
        <Loader2 className="size-4 animate-spin" />
        Loading solution taxonomy…
      </div>
    )
  }

  if (error && !taxonomy) {
    return (
      <div className={shell}>
        {!embedded && <h1 className="text-xl font-semibold text-foreground mb-2">Solutions</h1>}
        <p className="text-destructive">{error}</p>
      </div>
    )
  }

  if (!taxonomy) {
    return (
      <div className={shell}>
        {!embedded && <h1 className="text-xl font-semibold text-foreground mb-2">Solutions</h1>}
        <p className="text-muted-foreground">
          {programName ? `${programName} has ` : ''}no solution taxonomy generated yet. Ask
          the agent to run a solution taxonomy pass for this program.
        </p>
      </div>
    )
  }

  const inProgress = taxonomy.status === 'pending' || taxonomy.status === 'in_progress'
  const manual = taxonomy.mode === 'manual'
  const families = taxonomy.families ?? []

  return (
    <div className={`${shell} space-y-8`}>
      {/* Intro: stats + instructional copy, with provenance + regenerate */}
      <header className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-6 w-full lg:w-[calc(50%-10px)]">
          <div className="flex gap-10">
            <div className="flex flex-col gap-1.5">
              <span className="text-2xl text-foreground">{acceptedFamilies.length}</span>
              <span className="text-base text-foreground">Accepted Families</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-2xl text-foreground">{totalTechIdsSelected}</span>
              <span className="text-base text-foreground">Technologies</span>
            </div>
          </div>
          {taxonomy.summary && (
            <div className="max-h-28 overflow-y-auto pr-1">
              <p className="whitespace-pre-wrap text-xs text-muted-foreground">
                {taxonomy.summary}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col items-start gap-1.5 lg:items-end shrink-0">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              <Search className="size-4" />
              Search existing Tech Tree
            </Button>
            {/* Hand-picked taxonomies switch to AI from the module page instead. */}
            {!manual && (
              <Button
                variant="outline"
                size="sm"
                onClick={regenerate}
                disabled={inProgress}
              >
                Run fresh market scan
              </Button>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            Last updated {new Date(taxonomy.created_at).toLocaleString()}
          </span>
        </div>
      </header>

      {inProgress && (
        <div className="p-4 rounded-lg border border-border bg-card">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Loader2 className="size-4 animate-spin" />
            <span>
              Solution generation in progress
              {taxonomy.job?.current_step_label
                ? ` — ${taxonomy.job.current_step_label}`
                : ''}
            </span>
          </div>
          {typeof taxonomy.job?.progress_percentage === 'number' && (
            <div className="mt-2 h-2 rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-primary transition-all"
                style={{
                  width: `${Math.min(100, Math.max(0, taxonomy.job.progress_percentage))}%`,
                }}
              />
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg border border-border bg-card text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Results grid */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-foreground">Results</h2>

        {families.length === 0 ? (
          manual ? (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-border p-6">
              <p className="text-sm text-muted-foreground">
                No solution families yet. Search or browse the existing tech tree to add them.
              </p>
              <Button size="sm" onClick={() => setPickerOpen(true)}>
                <Search className="size-4" />
                Search existing Tech Tree
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This taxonomy has no families yet.
            </p>
          )
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
            {families.map((family) => {
              const accepted = family.status === 'accepted'
              const pending = pendingFamilyIds.has(family.id)
              const open = () => setSelectedFamilyId(family.id)
              return (
                <div
                  key={family.id}
                  role="button"
                  tabIndex={0}
                  onClick={open}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      open()
                    }
                  }}
                  className="flex flex-col gap-6 rounded-lg border border-border bg-card p-4 text-left cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-base font-medium text-foreground break-words">
                        {family.name}
                      </h3>
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={accepted}
                          disabled={pending}
                          onCheckedChange={(next) => toggleAccept(family, next)}
                          aria-label={`Activate ${family.name} for this program`}
                        />
                      </span>
                    </div>
                    <FamilyStats family={family} />
                  </div>

                  <div className="flex flex-col gap-4">
                    <FamilyAgeRisk family={family} className="text-xs" />
                    {family.description && (
                      <p className="text-xs leading-normal text-muted-foreground line-clamp-4">
                        {family.description}
                      </p>
                    )}
                  </div>

                  <span className="text-xs text-muted-foreground">More information</span>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <SolutionFamilyDetailModal
        family={selectedFamily}
        isOpen={selectedFamily != null}
        onClose={() => setSelectedFamilyId(null)}
        onToggleAccept={toggleAccept}
        onRunResearch={runResearch}
        toggling={selectedFamily ? pendingFamilyIds.has(selectedFamily.id) : false}
        researching={selectedFamily ? researchBusyIds.has(selectedFamily.id) : false}
        researchResult={selectedFamily ? researchResults[selectedFamily.id] : undefined}
      />

      <TechTreePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        familyStatusByTechId={familyStatusByTechId}
        onAdd={addFamilyFromTech}
        addDisabledReason={
          inProgress ? 'Solution generation is running — add once it finishes.' : null
        }
      />
    </div>
  )
}

export default ProgramSolutions
